import { usePostEquipmentApi, usePutEquipmentApi } from 'api/equipment'
import { Flex } from 'components/shared/flex'
import { CheckboxField, ChoiceField, InputField, SelectField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { Equipment, EquipmentForm } from 'entities/equipment'
import { useFetchEquipmentCategoriesApi } from 'api/equipment_category'

export type FormViewProps = {
    form: Form<EquipmentForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    const equipmentCategoriesApi = useFetchEquipmentCategoriesApi()

    useEffect(() => {
        equipmentCategoriesApi.execute()
    }, [])
    return (
        <>
            <Flex>
                <InputField form={form} attr="name" apiError={apiError} label="機器名称" required />
                <InputField form={form} attr="number" apiError={apiError} label="型番" required />
            </Flex>
            <Flex>
                <SelectField
                    style={{ width: 228 }}
                    form={form}
                    attr="equipmentCategoryId"
                    apiError={apiError}
                    label="機器カテゴリー"
                    selectItems={equipmentCategoriesApi.response.results.map((category) => {
                        return { label: `${category.name}  (LFRM-${category.code})`, value: category.id! }
                    })}
                    required
                    includeBlank
                />

                <ChoiceField form={form} attr="originalDocument" apiError={apiError} label="親文書番号" required tableName="equipment" />
            </Flex>
            <Flex>
                <InputField form={form} attr="equipmentAlias" apiError={apiError} label="衛生No" placeholder="衛生①" />
                <SelectField
                    style={{ width: 228 }}
                    form={form}
                    attr="type"
                    includeBlank
                    selectItems={[
                        { label: 'EXTRACTION', value: 'EXTRACTION' },
                        { label: 'PCR', value: 'PCR' },
                    ]}
                    apiError={apiError}
                    label="用途"
                />
            </Flex>

            <CheckboxField form={form} attr="useTemperatureLedger" checkboxLabel="温度管理台帳の対象" />
            {form.object.useTemperatureLedger && (
                <>
                    <Flex>
                        <SelectField
                            style={{ width: 228 }}
                            form={form}
                            attr="ondotoriSeries"
                            apiError={apiError}
                            label="おんどとり シリーズ"
                            selectItems={[
                                { label: 'TR7', value: 'TR7' },
                                { label: 'RTR500', value: 'RTR500' },
                            ]}
                            required
                            includeBlank
                        />
                        {form.object.ondotoriSeries === 'RTR500' && (
                            <SelectField
                                form={form}
                                attr="ondotoriParentSerialNumber"
                                apiError={apiError}
                                label="おんどとり 親機"
                                selectItems={[
                                    { label: '506親機', value: '5858065A' },
                                    { label: '702親機', value: '5858062F' },
                                    { label: '706親機', value: '58580F44' },
                                    { label: '711親機', value: '5858168E' },
                                    { label: '712親機', value: '58581697' },
                                ]}
                                required
                                includeBlank
                            />
                        )}
                    </Flex>
                    <InputField
                        form={form}
                        attr="ondotoriSerialNumber"
                        apiError={apiError}
                        label="おんどとり シリアル番号"
                        hint={
                            <div>
                                <div>おんどとりに登録されている機器は</div>
                                <div>シリアル番号を入力してください。</div>
                            </div>
                        }
                    />
                </>
            )}
        </>
    )
}

type NewEquipmentFormProps = {
    onComplete?: () => void
}

export const NewEquipmentForm = (props: NewEquipmentFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostEquipmentApi()
    const form = useForm<EquipmentForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>機器の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditEquipmentFormProps = {
    equipment: Equipment
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditEquipmentForm = (props: EditEquipmentFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutEquipmentApi()
    const form = useForm<EquipmentForm>(props.equipment)

    useEffectSkipFirst(() => {
        form.set(props.equipment)
    }, [props.equipment])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>機器の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
